import { useSelector } from "react-redux";
import { __RouterContext as RouterContext } from "react-router";
import { useCallback, useContext, useMemo } from "react";
import config from "@Config/index";

import {
  addLanguageToPath,
  removeLanguageFromPath,
  isChinaDomainClient,
  getDomainForLanguage,
  extractLanguageFromPath,
  isEnglishOnlyPath,
} from "@Utils/languageCore";

/**
 * Safe location for paths rendered outside the Router tree (e.g. map InfoWindows
 * mounted via ReactDOM.render). Falls back to window.location / "/".
 */
const useSafeLocationPathname = (): string => {
  // useLocation() crashes when Router context is missing; read context optionally instead.
  const routerContext = useContext(RouterContext) as { location?: { pathname?: string } } | null;
  if (routerContext?.location?.pathname) {
    return routerContext.location.pathname;
  }
  if (typeof window !== "undefined" && window.location?.pathname) {
    return window.location.pathname;
  }
  return "/";
};

/**
 * Unified hook for language-aware URL path handling.
 *
 * This is the SINGLE SOURCE OF TRUTH for determining whether a URL path
 * should have a language prefix added.
 *
 * Rules:
 * 1. If IS_CHINA_APP = true → NO prefix (Chinese is default)
 * 2. If userLanguage = "zh" → NO prefix (uses .cn domain instead)
 * 3. If userLanguage = "en" → NO prefix (English is default)
 * 4. Otherwise → ADD prefix (e.g., /fr/, /es/, /de/)
 *
 * @example
 * const { getLocalizedPath, shouldAddPrefix, userLanguage } = useLanguagePath();
 *
 * // Get the correct path with language prefix if needed
 * const path = getLocalizedPath("/places/search/london");
 * // Returns "/fr/places/search/london" if userLanguage is "fr"
 * // Returns "/places/search/london" if userLanguage is "zh", "en", or IS_CHINA_APP
 */
export const useLanguagePath = () => {
  const pathname = useSafeLocationPathname();
  const reduxLanguage = useSelector((state: any) => state.appData?.userLanguage || "en");
  const isChinaApp = config.IS_CHINA_APP || isChinaDomainClient();

  // The address bar is the source of truth for the page being viewed. After a
  // gesture-back, Redux can still be "fr" while the URL has no prefix — prefixing
  // from Redux would send the next click to /fr/... and skip the language modal.
  const userLanguage = useMemo(() => {
    if (isChinaApp) return reduxLanguage || "zh";
    return extractLanguageFromPath(pathname) || "en";
  }, [isChinaApp, reduxLanguage, pathname]);

  /**
   * Determine if language prefix should be added to URLs
   *
   * Returns FALSE for:
   * - China app (IS_CHINA_APP = true)
   * - China domain (.cn)
   * - Chinese language (zh)
   * - English language (en) - default, no prefix
   *
   * Returns TRUE for:
   * - French (fr), Spanish (es), German (de), etc.
   */
  const shouldAddPrefix = useMemo(() => {
    // Never add prefix for China app
    if (isChinaApp) return false;

    // Never add prefix for Chinese (uses domain instead)
    if (userLanguage === "zh") return false;

    // Never add prefix for English (default language)
    if (userLanguage === "en") return false;

    // All other languages need prefix
    return true;
  }, [isChinaApp, userLanguage]);

  /**
   * Get the localized path with language prefix if needed.
   *
   * @param path - The base path (e.g., "/places/search/london")
   * @param options - Optional configuration
   * @returns The path with language prefix if applicable
   *
   * @example
   * getLocalizedPath("/places/search/london")
   * // Returns "/fr/places/search/london" if French
   * // Returns "/places/search/london" if Chinese/English
   */
  const getLocalizedPath = useCallback(
    (
      path: string,
      options?: {
        /** Force skip prefix (for external links) */
        skipPrefix?: boolean;
        /** Override the language to use */
        overrideLanguage?: string;
      },
    ): string => {
      if (!path) return path;

      if (typeof path === "string" && (path.startsWith("#") || /^https?:\/\//i.test(path))) {
        return path;
      }

      const { skipPrefix = false, overrideLanguage } = options || {};

      // Use override language if provided, otherwise use user language
      const lang = overrideLanguage || userLanguage;

      // Skip prefix if explicitly requested, or if the target only exists in English
      if (skipPrefix || isEnglishOnlyPath(path)) {
        return removeLanguageFromPath(path);
      }

      // Never add prefix for China app
      if (isChinaApp) {
        return removeLanguageFromPath(path);
      }

      // Never add prefix for Chinese or English - ALWAYS remove it
      if (lang === "zh" || lang === "en") {
        // Remove any existing language prefix (including /en/)
        return removeLanguageFromPath(path);
      }

      // For other languages (fr, es, de), first remove any existing prefix
      // then add the correct one
      const cleanPath = removeLanguageFromPath(path);
      return addLanguageToPath(cleanPath, lang);
    },
    [userLanguage, isChinaApp],
  );

  /**
   * Check if navigation to the target language requires a cross-domain redirect.
   * This is needed when switching to/from Chinese.
   */
  const needsCrossDomain = useCallback(
    (targetLanguage: string): boolean => {
      // China app switching to non-Chinese
      if (isChinaApp && targetLanguage !== "zh") return true;

      // Non-China switching to Chinese
      if (!isChinaApp && targetLanguage === "zh") return true;

      return false;
    },
    [isChinaApp],
  );

  /**
   * Get the full URL for cross-domain navigation (e.g., switching to Chinese)
   */
  const getCrossDomainUrl = useCallback(
    (path: string, targetLanguage: string): string | null => {
      if (!needsCrossDomain(targetLanguage)) return null;

      const targetDomain = getDomainForLanguage(targetLanguage);
      if (!targetDomain) return null;

      const cleanPath = removeLanguageFromPath(path);
      return `${targetDomain}${cleanPath}`;
    },
    [needsCrossDomain],
  );

  return {
    /** Current user language from Redux */
    userLanguage,

    /** Whether this is a China app/domain */
    isChinaApp,

    /** Whether URLs should have language prefix added */
    shouldAddPrefix,

    /** Get the localized path with prefix if needed */
    getLocalizedPath,

    /** Check if target language requires cross-domain navigation */
    needsCrossDomain,

    /** Get full URL for cross-domain navigation */
    getCrossDomainUrl,
  };
};

export default useLanguagePath;
